import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowUpDown,
  ArrowRight,
  Bell,
  ChevronDown,
  Info,
  LockKeyhole,
  LockKeyholeOpen,
  Plus,
  TrendingUp,
} from "lucide-react";

import { useAuth } from "../context/AuthContext";
import {
  useAvailableBalances,
  useMarketAssets,
  usePortfolioEstimateAssets,
  usePriceHistory,
  useUserTransactionHistory,
  useZar8YieldHistory,
  useZar8YieldPositions,
} from "../hooks/query/queries";
import { cn } from "../lib/utils";
import alertIcon from "../assets/images/assetdashboard/alert.svg";
import depositIcon from "../assets/images/assetdashboard/deposit.svg";
import coinMarketCap from "../assets/images/assetdashboard/coinMarketCap.png";
import Toaster from "./ui/toaster";
import Zar8AssetPerformanceChart from "./Zar8AssetPerformanceChart";
import Zar8LockFlow from "./Zar8LockFlow";
import Zar8UnlockFlow from "./Zar8UnlockFlow";
import {
  BackButton,
  ZAR8_YIELD_PRIMARY_BUTTON_BACKGROUND,
} from "./ui/zar8-yield-flow-ui";
import CurrencyFormatter from "../utils/currencyFormatter";
import {
  calculateYieldPortfolioMetrics,
  getYieldMarketDataBySymbol,
} from "../utils/zar8Yield";
import {
  buildZar8YieldActivePositions,
  getZar8YieldTotalLockedValue,
} from "../utils/zar8YieldOverview";
import {
  getZar8YieldHistoryFromResponse,
  getZar8YieldPositionsFromResponse,
  getZar8YieldTotalLockedFromResponse,
  mapZar8YieldPositionsToLedgerEntries,
} from "../utils/zar8YieldApi";
import {
  clearZar8LockFlowState,
  createDefaultZar8LockFlowState,
  getZar8YieldFlowPath,
  persistZar8LockFlowState,
  readZar8LockFlowState,
  resolveZar8LockFlowState,
  sanitizeZar8AmountInput,
  ZAR8_LOCK_FLOW_STEPS,
  ZAR8_YIELD_FLOW_TYPES,
} from "../utils/zar8LockFlow";
import {
  formatFiatWithTwoDecimals,
  formatWithSignificantDigits,
  parseNumericValue,
} from "../utils/numberFormatter";
import {
  getZar8YieldCalculatorAvailableBalanceZar,
  getZar8YieldCalculatorZar8Price,
} from "../utils/zar8YieldCalculator";
import { buildZar8YieldPositionHistory } from "../utils/zar8YieldPosition";
import {
  buildZar8AssetPerformanceSeries,
  buildZar8AssetYieldHistory,
  emitZar8AssetPageEvent,
  getMarketAssetBySymbol,
  getZar8AssetBalanceValue,
  getZar8AssetPerformanceDelta,
  getZar8AssetPriceHistoryConfig,
  ZAR8_ASSET_DESCRIPTION,
  ZAR8_ASSET_TIME_FILTERS,
} from "../utils/zar8AssetPage";
import {
  formatMarketCap,
  formatPercentOfATH,
  formatPrice,
  formatSupply,
  formatVolume,
} from "../utils/marketDataFormatter";

const NOISE_TEXTURE = "/figma/zar8-yield/noise.png";
const BALANCE_BACKGROUND = "/figma/zar8-yield/balance-background.svg";
const ZAR8_CIRCLE = "/figma/zar8-yield/zar8-circle.svg";
const ZAR8_MARK_SMALL = "/figma/zar8-yield/zar8-mark-small.png";
const BALANCE_BASE_GRADIENT =
  "linear-gradient(180deg, rgba(95, 117, 255, 0.95) 0%, rgba(151, 71, 255, 0.98) 53%, rgba(235, 51, 102, 0.96) 100%)";

const formatPercentageValue = (value) =>
  parseNumericValue(value).toFixed(2).replace(/\.00$/, "");

const formatCompactZarAmount = (value) => {
  const normalizedValue = parseNumericValue(value);

  if (!normalizedValue) {
    return "R0";
  }

  const hasDecimals = Math.abs(normalizedValue % 1) > 0;
  const formattedValue = hasDecimals
    ? normalizedValue
        .toFixed(2)
        .replace(/\B(?=(\d{3})+(?!\d))/g, " ")
        .replace(/\.00$/, "")
    : `${Math.round(normalizedValue)}`.replace(/\B(?=(\d{3})+(?!\d))/g, " ");

  return `R${formattedValue}`;
};

const SERVICE_CHARGE_RATE = 0.02;

const buildZar8HoldingMetrics = ({
  rawMetrics,
  totalAmount,
  totalValue,
  totalPortfolioValue,
}) => {
  const hasCostBasis =
    rawMetrics.netInvested > 0 ||
    rawMetrics.averageCost > 0 ||
    rawMetrics.totalDeposited > 0;

  if (hasCostBasis || totalValue <= 0) {
    return rawMetrics;
  }

  const averageCost = totalAmount > 0 ? totalValue / totalAmount : 1;
  const portfolioDiversity =
    totalPortfolioValue > 0 ? (totalValue / totalPortfolioValue) * 100 : 0;

  return {
    ...rawMetrics,
    averageCost,
    netInvested: totalValue,
    unrealizedPnL: 0,
    portfolioDiversity,
    totalPnL: 0,
    pnlPercentage: 0,
    totalDeposited: totalValue,
  };
};

const formatHistoryBtcEquivalent = (value) =>
  formatWithSignificantDigits(parseNumericValue(value), {
    minSignificant: 4,
    maxDecimals: 8,
  });

function AssetHeaderButton({
  icon,
  fallbackIcon: FallbackIcon,
  label,
  onClick,
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex h-12 items-center gap-2 rounded-full border border-[#EBE9EF] bg-white px-6 text-sm font-semibold leading-5 text-[#6D6584] transition-colors hover:bg-[#FAF9FC]"
    >
      {icon ? (
        <img src={icon} alt="" aria-hidden="true" className="h-4 w-4" />
      ) : FallbackIcon ? (
        <FallbackIcon className="h-4 w-4" />
      ) : null}
      <span>{label}</span>
    </button>
  );
}

function TimeFilterButton({ value, activeValue, onClick }) {
  const isActive = value === activeValue;

  return (
    <button
      type="button"
      onClick={() => onClick(value)}
      className={cn(
        "flex h-10 min-w-[62px] items-center justify-center rounded-full px-6 text-sm font-normal leading-5 transition-colors",
        isActive
          ? "bg-[#33125F] text-white"
          : "text-[#9D96AF] hover:bg-[#FAF9FC]"
      )}
    >
      {value}
    </button>
  );
}

function PanelTabs({ value, onChange }) {
  return (
    <div className="rounded-full border border-[#EBE9EF] p-2">
      <div className="grid grid-cols-2 gap-2">
        {[ZAR8_YIELD_FLOW_TYPES.lock, ZAR8_YIELD_FLOW_TYPES.unlock].map(
          (tabValue) => {
            const isActive = value === tabValue;

            return (
              <button
                key={tabValue}
                type="button"
                onClick={() => onChange(tabValue)}
                className={cn(
                  "flex h-12 items-center justify-center rounded-full text-sm font-semibold leading-5 transition-colors",
                  isActive
                    ? "bg-[#33125F] text-white"
                    : "text-[#9D96AF] hover:bg-[#FAF9FC]"
                )}
              >
                {tabValue === ZAR8_YIELD_FLOW_TYPES.lock ? "Lock" : "Unlock"}
              </button>
            );
          }
        )}
      </div>
    </div>
  );
}

function HistoryIcon({ type }) {
  if (type === "yield") {
    return <TrendingUp className="h-5 w-5 text-white" />;
  }

  if (type === "unlocked") {
    return <LockKeyholeOpen className="h-5 w-5 text-white" />;
  }

  return <LockKeyhole className="h-5 w-5 text-white" />;
}

function HistoryCard({ item }) {
  return (
    <div className="rounded-2xl border border-[#F1EDF7] bg-white px-4 py-4 backdrop-blur-[20px]">
      <div className="flex min-h-[52px] items-center justify-between gap-6">
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#EB3366]">
            <HistoryIcon type={item.type} />
          </div>
          <div className="min-w-0">
            <p className="text-base font-semibold leading-6 text-[#423E51]">
              {item.label}
            </p>
            <p className="mt-3 text-xs leading-[14px] text-[#6D6584]">
              {item.createdAt
                ? new Date(item.createdAt).toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  })
                : "Recently"}
            </p>
          </div>
        </div>

        <div className="shrink-0 text-right">
          <p className="text-base font-semibold leading-6 text-[#423E51]">
            {formatCompactZarAmount(item.amountZar)}
          </p>
          <p className="mt-3 text-xs leading-[14px] text-[#6D6584]">
            ≈ BTC {formatHistoryBtcEquivalent(item.amountBtc)}
          </p>
          <p className="mt-[3px] text-[10px] font-bold uppercase leading-[14px] tracking-[0.5px] text-[#17C3A5]">
            {item.status}
          </p>
        </div>
      </div>
    </div>
  );
}

function MarketDatum({ label, value, accent = false }) {
  return (
    <div className="w-[123px]">
      <p className="text-xs leading-[14px] text-[#6D6584]">{label}</p>
      <p
        className={cn(
          "mt-[10px] text-base font-semibold leading-6 text-[#423E51]",
          accent && "text-[20px] leading-6 text-[#17C3A5]"
        )}
      >
        {value}
      </p>
    </div>
  );
}

function BreakdownTile({ label, value, negative = false }) {
  return (
    <div
      className="flex h-[112px] flex-col justify-start rounded-[20px] border border-[rgba(255,255,255,0.32)] px-[25px] pt-[30px] backdrop-blur-[9.4px]"
      style={{
        background: "rgba(255,255,255,0.11)",
      }}
    >
      <p className="text-base leading-6 text-[#EBE9EF]">{label}</p>
      <p
        className={cn(
          "mt-[9px] text-[24px] font-semibold leading-7 text-[#EBE9EF]",
          negative && "text-[#FFF2EB]"
        )}
      >
        {value}
      </p>
    </div>
  );
}

export default function Zar8AssetPageContent({
  basePath = "/zar8/asset",
  routeFlowType = null,
}) {
  const navigate = useNavigate();
  const { settings, userProfile } = useAuth();
  const userId = userProfile?._id;
  const formatter = useMemo(() => new CurrencyFormatter(), []);
  const [lockFlowState, setLockFlowState] = useState(() =>
    readZar8LockFlowState()
  );
  const [selectedTab, setSelectedTab] = useState(ZAR8_YIELD_FLOW_TYPES.lock);
  const [amountInput, setAmountInput] = useState("");
  const [amountError, setAmountError] = useState("");
  const [timeFilter, setTimeFilter] = useState("1W");
  const resolvedFlowState = useMemo(
    () => resolveZar8LockFlowState(lockFlowState, routeFlowType),
    [lockFlowState, routeFlowType]
  );

  const { data: availableBalances } = useAvailableBalances(userId);
  const positionQueryParams = useMemo(
    () => ({
      status: "active",
      page: 1,
      limit: 100,
    }),
    []
  );
  const historyQueryParams = useMemo(
    () => ({
      page: 1,
      limit: 20,
    }),
    []
  );
  const { data: zar8YieldPositions } = useZar8YieldPositions(
    positionQueryParams,
    !!userId
  );
  const { data: zar8YieldHistory } = useZar8YieldHistory(
    historyQueryParams,
    !!userId
  );
  const { data: marketAssets } = useMarketAssets();
  const { data: userTransactions } = useUserTransactionHistory(userId, true, {
    fetchAllPages: false,
  });
  const { data: portfolioEstimateAssets } = usePortfolioEstimateAssets();
  const priceHistoryConfig = getZar8AssetPriceHistoryConfig(timeFilter);
  const {
    data: priceHistoryData,
    isLoading: isPriceHistoryLoading,
    isError: hasPriceHistoryError,
    refetch: refetchPriceHistory,
  } = usePriceHistory(
    "ZAR8",
    priceHistoryConfig.interval,
    priceHistoryConfig.range
  );

  const zar8Asset = useMemo(
    () =>
      availableBalances?.find(
        (asset) => asset?.asset?.toLowerCase() === "zar8"
      ) || null,
    [availableBalances]
  );
  const activeYieldEntries = useMemo(
    () =>
      mapZar8YieldPositionsToLedgerEntries(
        getZar8YieldPositionsFromResponse(zar8YieldPositions)
      ),
    [zar8YieldPositions]
  );
  const yieldHistoryItems = useMemo(
    () => getZar8YieldHistoryFromResponse(zar8YieldHistory),
    [zar8YieldHistory]
  );
  const yieldPositions = useMemo(
    () => buildZar8YieldActivePositions(activeYieldEntries),
    [activeYieldEntries]
  );
  const totalLockedValue = useMemo(
    () =>
      getZar8YieldTotalLockedFromResponse(zar8YieldPositions) ||
      getZar8YieldTotalLockedValue(yieldPositions),
    [yieldPositions, zar8YieldPositions]
  );
  const zar8PriceInZar = getZar8YieldCalculatorZar8Price(zar8Asset);
  const availableBalanceZar =
    getZar8YieldCalculatorAvailableBalanceZar(zar8Asset);
  const totalBalanceZar = getZar8AssetBalanceValue({
    zar8Asset,
    marketAssets,
  });
  const totalZar8HoldingAmount =
    parseNumericValue(zar8Asset?.availableAmount) + totalLockedValue;
  const totalZar8HoldingValue = totalBalanceZar + totalLockedValue;
  const totalZar8Asset = useMemo(
    () =>
      zar8Asset
        ? {
            ...zar8Asset,
            availableAmount: totalZar8HoldingAmount,
            valueInLocalCurrency: totalZar8HoldingValue,
            valueInZar: totalZar8HoldingValue,
          }
        : {
            asset: "zar8",
            availableAmount: totalZar8HoldingAmount,
            valueInLocalCurrency: totalZar8HoldingValue,
            valueInZar: totalZar8HoldingValue,
          },
    [totalZar8HoldingAmount, totalZar8HoldingValue, zar8Asset]
  );
  const totalPortfolioValue = parseNumericValue(
    portfolioEstimateAssets?.zarEstimate ??
      portfolioEstimateAssets?.portfolioEstimateRaw
  );
  const totalPortfolioValueWithLockedZar8 =
    totalPortfolioValue + totalLockedValue;
  const portfolioMetrics = useMemo(() => {
    const rawMetrics = calculateYieldPortfolioMetrics({
      transactions: userTransactions,
      assetSymbol: "ZAR8",
      assetBalance: totalZar8Asset,
      totalPortfolioValue: totalPortfolioValueWithLockedZar8,
    });

    return buildZar8HoldingMetrics({
      rawMetrics,
      totalAmount: totalZar8HoldingAmount,
      totalValue: totalZar8HoldingValue,
      totalPortfolioValue: totalPortfolioValueWithLockedZar8,
    });
  }, [
    totalPortfolioValueWithLockedZar8,
    totalZar8Asset,
    totalZar8HoldingAmount,
    totalZar8HoldingValue,
    userTransactions,
  ]);
  const zar8MarketData = useMemo(
    () => getYieldMarketDataBySymbol(marketAssets, "ZAR8"),
    [marketAssets]
  );
  const zar8MarketAsset = useMemo(
    () => getMarketAssetBySymbol(marketAssets, "ZAR8"),
    [marketAssets]
  );
  const zar8CurrentPrice = parseNumericValue(
    zar8MarketData?.priceInZar ??
      zar8MarketData?.price ??
      zar8MarketAsset?.priceInZar ??
      zar8MarketAsset?.price
  );
  const performanceSeries = useMemo(
    () =>
      buildZar8AssetPerformanceSeries({
        priceHistoryData,
        timeFilter,
        zar8Asset,
        marketAssets,
      }),
    [marketAssets, priceHistoryData, timeFilter, zar8Asset]
  );
  const performanceDelta = useMemo(
    () => getZar8AssetPerformanceDelta(performanceSeries),
    [performanceSeries]
  );
  const yieldApiHistoryEntries = useMemo(
    () =>
      buildZar8YieldPositionHistory({
        history: yieldHistoryItems,
        marketAssets,
      }).filter((item) =>
        ["locked", "unlocked", "yield"].includes(item?.type || "")
      ),
    [marketAssets, yieldHistoryItems]
  );
  const historyEntries = useMemo(() => {
    const combinedHistory = buildZar8AssetYieldHistory({
      activeEntries: activeYieldEntries,
      pendingEntries: [],
      yieldHistory: yieldHistoryItems,
      userTransactions: userTransactions || [],
      marketAssets,
    });

    return combinedHistory.length > 0
      ? combinedHistory
      : yieldApiHistoryEntries.slice(0, 5);
  }, [
    activeYieldEntries,
    marketAssets,
    userTransactions,
    yieldApiHistoryEntries,
    yieldHistoryItems,
  ]);
  const panelBalance =
    selectedTab === ZAR8_YIELD_FLOW_TYPES.unlock
      ? totalLockedValue
      : availableBalanceZar;
  const canContinue = parseNumericValue(amountInput) > 0 && !amountError;
  const approximateZarValue = parseNumericValue(amountInput);
  const btcPriceInZar = parseNumericValue(
    getMarketAssetBySymbol(marketAssets, "BTC")?.priceInZar ??
      getMarketAssetBySymbol(marketAssets, "BTC")?.info?.zarprice ??
      getMarketAssetBySymbol(marketAssets, "BTC")?.price
  );
  const approximateBtcEquivalent =
    approximateZarValue > 0 && btcPriceInZar > 0
      ? (approximateZarValue * zar8PriceInZar) / btcPriceInZar
      : 0;
  const serviceChargeAmount = approximateZarValue * SERVICE_CHARGE_RATE;

  const transformedZar8Asset = useMemo(
    () =>
      zar8Asset
        ? {
            ...zar8Asset,
            assetId:
              zar8Asset.assetId ||
              zar8Asset.id ||
              zar8Asset._id ||
              zar8Asset.asset,
            assetIcon: zar8Asset.assetIcon || zar8Asset.image,
            asset: zar8Asset.asset || "zar8",
            assetName: zar8Asset.assetName || "ZAR8",
            currencyType: zar8Asset.currencyType || zar8Asset.type,
            type: zar8Asset.type || zar8Asset.currencyType,
          }
        : null,
    [zar8Asset]
  );

  useEffect(() => {
    if (resolvedFlowState?.isOpen) {
      persistZar8LockFlowState(resolvedFlowState);
      return;
    }

    clearZar8LockFlowState();
  }, [resolvedFlowState]);

  useEffect(() => {
    emitZar8AssetPageEvent("zar8_asset_page_viewed", {
      balanceZar: totalBalanceZar,
      lockedBalanceZar: totalLockedValue,
    });
  }, [totalBalanceZar, totalLockedValue]);

  const handleSwitchTab = (nextTab) => {
    setSelectedTab(nextTab);
    setAmountError("");
    setAmountInput("");
    emitZar8AssetPageEvent("zar8_asset_tab_switched", {
      tab: nextTab,
    });
  };

  const handleAmountChange = (nextValue) => {
    const sanitizedValue = sanitizeZar8AmountInput(nextValue);
    const numericValue = parseNumericValue(sanitizedValue);
    const availableLimit =
      selectedTab === ZAR8_YIELD_FLOW_TYPES.unlock
        ? totalLockedValue
        : availableBalanceZar;

    setAmountInput(sanitizedValue);

    if (numericValue <= 0 && sanitizedValue) {
      setAmountError("Enter an amount greater than zero.");
      return;
    }

    if (availableLimit > 0 && numericValue > availableLimit) {
      setAmountError(
        selectedTab === ZAR8_YIELD_FLOW_TYPES.unlock
          ? "Amount exceeds your locked ZAR8 balance."
          : "Amount exceeds your available ZAR8 balance."
      );
      return;
    }

    setAmountError("");
  };

  const handleOpenFlow = () => {
    const nextFlowType = selectedTab;
    const nextState = {
      ...createDefaultZar8LockFlowState(),
      isOpen: true,
      flowType: nextFlowType,
      step: ZAR8_LOCK_FLOW_STEPS.amount,
      amountInput,
      amountMode: "zar",
    };

    emitZar8AssetPageEvent(
      nextFlowType === ZAR8_YIELD_FLOW_TYPES.lock
        ? "zar8_asset_lock_initiated"
        : "zar8_asset_unlock_initiated",
      {
        amountZar: parseNumericValue(amountInput),
      }
    );

    window.scrollTo(0, 0);
    setLockFlowState(nextState);
    navigate(getZar8YieldFlowPath(basePath, nextFlowType));
  };

  const handleModeChange = (nextFlowType) => {
    window.scrollTo(0, 0);
    setSelectedTab(nextFlowType);
    setLockFlowState({
      ...createDefaultZar8LockFlowState(),
      isOpen: true,
      flowType: nextFlowType,
    });
    navigate(getZar8YieldFlowPath(basePath, nextFlowType));
  };

  const handleCloseFlow = () => {
    clearZar8LockFlowState();
    setLockFlowState(createDefaultZar8LockFlowState());
    window.scrollTo(0, 0);
    navigate(basePath);
  };

  const handleDeposit = () => {
    navigate("/trading/digitalWallet", {
      state: {
        tabActive: "Deposit",
        selectedTab: "deposit",
        selectedCrypto: transformedZar8Asset || {
          asset: "zar8",
          symbol: "ZAR8",
          assetName: "ZAR8",
        },
        type: "withdraw-deposit",
      },
    });
  };

  const handleSetAlert = () => {
    emitZar8AssetPageEvent("zar8_asset_set_alert_clicked", {
      asset: "ZAR8",
    });
  };

  if (resolvedFlowState?.isOpen) {
    return (
      <main>
        <Toaster />
        {resolvedFlowState.flowType === ZAR8_YIELD_FLOW_TYPES.unlock ? (
          <Zar8UnlockFlow
            flowState={resolvedFlowState}
            onFlowStateChange={setLockFlowState}
            activeYieldEntries={activeYieldEntries}
            zar8Asset={zar8Asset}
            isTwoFactorEnabled={Boolean(
              settings?.security?.twoFactorAuth?.enabled
            )}
            onClose={handleCloseFlow}
            onModeChange={handleModeChange}
          />
        ) : (
          <Zar8LockFlow
            flowState={resolvedFlowState}
            onFlowStateChange={setLockFlowState}
            availableBalanceZar={availableBalanceZar}
            zar8Asset={zar8Asset}
            isTwoFactorEnabled={Boolean(
              settings?.security?.twoFactorAuth?.enabled
            )}
            onClose={handleCloseFlow}
            onModeChange={handleModeChange}
          />
        )}
      </main>
    );
  }

  return (
    <main className="px-5 pb-12 pt-7 md:px-8 xl:px-[74px] xl:pb-[42px] xl:pt-10">
      <Toaster />

      <div className="mx-auto max-w-[1295px]">
        <div className="flex flex-col gap-8 xl:flex-row xl:items-center xl:justify-between">
          <div className="flex items-center gap-[18px]">
            <BackButton onClick={() => navigate(-1)} />

            <div className="flex items-center gap-4">
              <span className="relative block h-[52px] w-[52px] shrink-0">
                <img
                  src={ZAR8_CIRCLE}
                  alt=""
                  aria-hidden="true"
                  className="block h-[52px] w-[52px]"
                />
                <img
                  src={ZAR8_MARK_SMALL}
                  alt=""
                  aria-hidden="true"
                  className="absolute left-[15px] top-[11px] h-[29px] w-[22px] object-contain"
                />
              </span>

              <h1 className="text-[40px] font-semibold leading-10 text-[#423E51]">
                ZAR8
              </h1>
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <AssetHeaderButton
              icon={alertIcon}
              fallbackIcon={Bell}
              label="Set Alert"
              onClick={handleSetAlert}
            />
            <AssetHeaderButton
              icon={depositIcon}
              fallbackIcon={ArrowRight}
              label="Deposit"
              onClick={handleDeposit}
            />
          </div>
        </div>

        <div className="mt-[46px] grid gap-[19px] xl:grid-cols-[719px_minmax(0,557px)]">
          <section className="rounded-[24px] border border-[#EBE9EF] bg-white px-8 pb-8 pt-6">
            <p className="text-[20px] font-semibold leading-7 text-[#6D6584]">
              Available balance
            </p>

            <div className="mt-3 flex flex-col gap-8">
              <div className="flex flex-wrap items-center gap-[7px]">
                <p className="text-[36px] font-bold leading-7 text-[#33125F]">
                  {formatCompactZarAmount(totalBalanceZar)}
                </p>
                <p
                  className={cn(
                    "text-sm font-semibold leading-5",
                    performanceDelta.deltaValue >= 0
                      ? "text-[#17C3A5]"
                      : "text-[#EB3366]"
                  )}
                >
                  {`${performanceDelta.deltaValue >= 0 ? "+" : "-"} ${formatCompactZarAmount(
                    Math.abs(performanceDelta.deltaValue)
                  )} (${formatPercentageValue(
                    Math.abs(performanceDelta.deltaPercentage)
                  )}%)`}
                </p>
              </div>

              <div className="flex w-fit flex-wrap gap-1 rounded-full border border-[#EBE9EF] p-1">
                {ZAR8_ASSET_TIME_FILTERS.map((filterValue) => (
                  <TimeFilterButton
                    key={filterValue}
                    value={filterValue}
                    activeValue={timeFilter}
                    onClick={setTimeFilter}
                  />
                ))}
              </div>
            </div>

            <div className="mt-6">
              <Zar8AssetPerformanceChart
                series={performanceSeries}
                isLoading={isPriceHistoryLoading}
                hasError={hasPriceHistoryError}
                onRetry={refetchPriceHistory}
              />
            </div>
          </section>

          <aside className="flex h-[694px] flex-col items-center justify-center gap-8 rounded-[24px] border border-[#EBE9EF] bg-white px-8 py-6">
            <p className="text-center text-[20px] font-semibold leading-7 text-[#57249C]">
              {selectedTab === ZAR8_YIELD_FLOW_TYPES.lock
                ? "Lock ZAR8"
                : "Unlock ZAR8"}
            </p>

            <div className="w-full">
              <PanelTabs value={selectedTab} onChange={handleSwitchTab} />
            </div>

            <div className="grid w-full gap-8">
              <div className="grid gap-6 xl:grid-cols-[minmax(0,238px)_minmax(0,238px)]">
                <div>
                  <p className="text-sm font-normal leading-5 text-[#6D6584]">
                    Asset
                  </p>
                  <div className="mt-3 flex h-[88px] items-center justify-between rounded-2xl border border-[#CECBD7] bg-white px-6">
                    <div className="flex items-center gap-3">
                      <span className="relative block h-10 w-10 shrink-0">
                        <img
                          src={ZAR8_CIRCLE}
                          alt=""
                          aria-hidden="true"
                          className="block h-10 w-10"
                        />
                        <img
                          src={ZAR8_MARK_SMALL}
                          alt=""
                          aria-hidden="true"
                          className="absolute left-[11px] top-[9px] h-[22px] w-[17px] object-contain"
                        />
                      </span>
                      <span className="text-xl font-semibold leading-7 text-[#423E51]">
                        ZAR8
                      </span>
                    </div>

                    <ChevronDown className="h-4 w-4 text-[#6D6584]" />
                  </div>
                </div>

                <div>
                  <p className="text-sm font-normal leading-5 text-[#6D6584]">
                    {selectedTab === ZAR8_YIELD_FLOW_TYPES.unlock
                      ? "Locked balance"
                      : "Available balance"}
                  </p>
                  <div className="mt-3 flex h-[88px] items-center rounded-2xl bg-[rgba(235,233,239,0.3)] px-6 text-xl font-semibold leading-6 text-[#6D6584]">
                    {formatFiatWithTwoDecimals(panelBalance)}
                  </div>
                </div>
              </div>

              <div>
                <p className="text-sm font-normal leading-5 text-[#6D6584]">
                  Enter amount
                </p>
                <div className="mt-3 rounded-2xl border border-[#CECBD7] px-6 py-[23px]">
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <input
                      value={amountInput}
                      onChange={(event) =>
                        handleAmountChange(event.target.value)
                      }
                      inputMode="decimal"
                      placeholder="0.00"
                      className="min-w-0 flex-1 bg-transparent text-[32px] font-semibold leading-10 text-[#33125F] outline-none placeholder:text-[#C4BED4]"
                    />
                    <div className="flex items-center gap-3">
                      <span className="text-sm leading-5 text-[#9D96AF]">
                        ≈{" "}
                        {formatWithSignificantDigits(approximateBtcEquivalent, {
                          minSignificant: 4,
                          maxDecimals: 8,
                        })}{" "}
                        BTC
                      </span>
                      <span className="flex h-9 w-9 items-center justify-center rounded-full border border-[#F1EDF7] text-[#9D96AF]">
                        <ArrowUpDown className="h-4 w-4" />
                      </span>
                      <button
                        type="button"
                        onClick={() =>
                          handleAmountChange(
                            panelBalance > 0 ? `${panelBalance}` : ""
                          )
                        }
                        className="flex h-9 items-center justify-center rounded-full border border-[#F1EDF7] px-4 text-sm font-semibold leading-5 text-[#6D6584] transition-colors hover:bg-[#FAF9FC]"
                      >
                        Max
                      </button>
                    </div>
                  </div>
                </div>

                {amountError ? (
                  <p className="mt-3 text-sm leading-5 text-[#EB3366]">
                    {amountError}
                  </p>
                ) : null}
              </div>

              <div className="w-full">
                <button
                  type="button"
                  onClick={handleOpenFlow}
                  disabled={!canContinue}
                  className={cn(
                    "h-12 w-full rounded-full px-6 text-sm font-semibold leading-5 text-white transition-opacity",
                    !canContinue && "cursor-not-allowed opacity-40"
                  )}
                  style={{
                    background: ZAR8_YIELD_PRIMARY_BUTTON_BACKGROUND,
                  }}
                >
                  Continue
                </button>
              </div>

              <div className="flex items-center justify-center gap-2 text-xs leading-4 text-[#6D6584]">
                <span>{`Service charge: 2% (Approx. ${formatFiatWithTwoDecimals(
                  serviceChargeAmount
                )})`}</span>
                <Info className="h-4 w-4 text-[#EB3366]" />
              </div>
            </div>
          </aside>
        </div>

        <div className="mt-[19px] grid gap-[19px] xl:grid-cols-[616px_minmax(0,660px)]">
          <section className="rounded-[24px] border border-[#EBE9EF] bg-white px-[30px] pb-8 pt-4">
            <div className="flex h-[47px] items-center justify-between py-4">
              <h2 className="text-[20px] font-semibold leading-7 text-[#6D6584]">
                Description
              </h2>
            </div>

            <p className="text-base leading-6 text-[#423E51]">
              {ZAR8_ASSET_DESCRIPTION}
            </p>

            <img
              src={coinMarketCap}
              alt="CoinMarketCap"
              className="mt-4 h-[35px] w-[119px] object-contain"
            />
          </section>

          <section className="rounded-[24px] border border-[#F1EDF7] bg-white px-7 py-[26px]">
            <h2 className="text-[20px] font-semibold leading-7 text-[#6D6584]">
              Market Data
            </h2>

            <div className="mt-[22px] flex flex-wrap gap-x-9 gap-y-[22px]">
              <MarketDatum
                label="Market Cap"
                value={formatMarketCap(zar8MarketData?.marketCap, "R")}
              />
              <MarketDatum
                label="24h Volume"
                value={formatVolume(zar8MarketData?.volume24h, "R")}
              />
              <MarketDatum
                label="Circulating"
                value={formatSupply(zar8MarketData?.circulatingSupply, "ZAR8")}
              />
              <MarketDatum
                label="Rank"
                value={
                  zar8MarketData?.rank != null ? `#${zar8MarketData.rank}` : "—"
                }
              />
              <MarketDatum
                label="Max Supply"
                value={formatSupply(zar8MarketData?.maxSupply, "ZAR8")}
              />
              <MarketDatum
                label="Total Supply"
                value={formatSupply(zar8MarketData?.totalSupply, "ZAR8")}
              />
              <MarketDatum
                label="All-Time High"
                value={formatPrice(zar8MarketData?.allTimeHigh, "R")}
              />
              <MarketDatum
                label="% of ATH"
                value={formatPercentOfATH(
                  zar8CurrentPrice,
                  parseNumericValue(zar8MarketData?.allTimeHigh)
                )}
              />
              <MarketDatum
                label="ROI"
                value={`${parseNumericValue(zar8MarketData?.roiPercentage) >= 0 ? "+" : ""}${formatPercentageValue(
                  parseNumericValue(zar8MarketData?.roiPercentage)
                )}%`}
                accent
              />
            </div>
          </section>
        </div>

        <div className="mt-[19px] grid gap-[26px] xl:grid-cols-[440px_minmax(0,829px)]">
          <section
            className="relative min-h-[672px] overflow-hidden rounded-[24px] border border-[#EBE9EF] xl:h-[672px]"
            style={{
              backgroundImage: BALANCE_BASE_GRADIENT,
            }}
          >
            <img
              src={BALANCE_BACKGROUND}
              alt=""
              aria-hidden="true"
              className="pointer-events-none absolute left-[-167px] top-[473px] max-w-none"
              style={{
                width: "760.727px",
                height: "581.944px",
              }}
            />
            <div
              className="absolute inset-0 mix-blend-overlay opacity-40"
              style={{
                backgroundImage: `url(${NOISE_TEXTURE})`,
                backgroundPosition: "top left",
                backgroundSize: "1697.34px 846.68px",
              }}
            />

            <div className="relative flex min-h-[672px] flex-col px-[29px] pb-8 pt-4 text-white xl:px-0 xl:pb-0 xl:pt-0">
              <div className="flex flex-col items-center rounded-[20px] px-[5px] py-[25px] text-center xl:absolute xl:left-[29px] xl:top-[15px] xl:h-[112px] xl:w-[380px] xl:justify-center">
                <p className="text-[20px] font-semibold leading-7 text-[#EBE9EF]">
                  My Balance
                </p>
                <div className="mt-[6px] flex flex-col items-center gap-[5px]">
                  <p className="text-[32px] font-semibold leading-10 text-[#EBE9EF]">
                    {formatter.formatCrypto(totalZar8HoldingAmount, "ZAR8")}
                  </p>
                  <p className="text-sm font-semibold leading-5 text-[#17C3A5]">
                    {`${portfolioMetrics.totalPnL >= 0 ? "+" : "-"} ${formatCompactZarAmount(
                      Math.abs(portfolioMetrics.totalPnL)
                    )} (${formatPercentageValue(
                      Math.abs(portfolioMetrics.pnlPercentage)
                    )}%)`}
                  </p>
                </div>
              </div>

              <div className="mt-4 grid gap-4 xl:absolute xl:bottom-[33px] xl:left-[29px] xl:w-[380px]">
                <BreakdownTile
                  label="Average cost"
                  value={formatter.format(portfolioMetrics.averageCost, "ZAR")}
                />
                <BreakdownTile
                  label="Net Invested"
                  value={formatter.format(portfolioMetrics.netInvested, "ZAR")}
                />
                <BreakdownTile
                  label="Unrealized Profit & Loss"
                  value={`${portfolioMetrics.unrealizedPnL >= 0 ? "" : "-"}${formatter.format(
                    Math.abs(portfolioMetrics.unrealizedPnL),
                    "ZAR"
                  )}`}
                  negative={portfolioMetrics.unrealizedPnL < 0}
                />
                <BreakdownTile
                  label="Portfolio Diversity"
                  value={`${formatPercentageValue(
                    portfolioMetrics.portfolioDiversity
                  )}%`}
                />
              </div>
            </div>
          </section>

          <section className="flex h-[672px] flex-col rounded-[24px] border border-[#EBE9EF] bg-white pb-8 pt-4">
            <div className="flex items-center justify-between px-[30px] py-4">
              <h2 className="text-[20px] font-semibold leading-7 text-[#6D6584]">
                Yield history
              </h2>
              <button
                type="button"
                onClick={() => {
                  setSelectedTab(ZAR8_YIELD_FLOW_TYPES.lock);
                  setAmountError("");
                  setAmountInput("");
                  emitZar8AssetPageEvent("zar8_asset_plus_yield_clicked");
                  navigate(
                    getZar8YieldFlowPath(basePath, ZAR8_YIELD_FLOW_TYPES.lock)
                  );
                }}
                className="flex h-12 items-center gap-2 rounded-full border border-[#EBE9EF] px-5 text-sm font-semibold leading-5 text-[#6D6584] transition-colors hover:bg-[#FAF9FC]"
              >
                <Plus className="h-4 w-4" />
                <span>Yield</span>
              </button>
            </div>

            <div className="flex-1 px-8">
              {historyEntries.length > 0 ? (
                <div className="space-y-4">
                  {historyEntries.map((item) => (
                    <HistoryCard key={item.id} item={item} />
                  ))}
                </div>
              ) : (
                <div className="flex min-h-[240px] items-center justify-center rounded-[24px] border border-dashed border-[#EBE9EF] bg-[#FAF9FC] px-6 text-center">
                  <div>
                    <p className="text-base font-semibold leading-6 text-[#423E51]">
                      No yield history yet
                    </p>
                    <p className="mt-2 text-sm leading-5 text-[#6D6584]">
                      Your ZAR8 lock, unlock, and payout activity will appear
                      here once transactions are available.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </section>
        </div>
      </div>
    </main>
  );
}
